import { useCallback, useMemo } from "react";
import { array, mixed, number, object, string, type ObjectSchema } from "yup";
import { Flex, ModalV2 } from "@jsq/ds/core";
import { Form, FormSelect, FormTextInput, SubmitButton } from "@jsq/ds/forms";
import { useAvailableReportTypeOptions } from "~/pages/Reporting/ReportTemplateEditor/types";
import { SPACING } from "~/styles/variables";
import { renderFormSelectOptions } from "../../shared/DataFormatSection/FormSelectOptions";
import { TransactionTypesField } from "../../shared/TransactionTypesField";
import {
  supportsTransactionTypes,
  toTransactionTypeNodeIds,
} from "../../shared/TransactionTypesField/supportedTemplateTypes";
import { FocusFirstFormErrorOnSubmit } from "../focusFirstFormError";
import type { TypeHierarchy } from "../DataPackageSlideout/HierarchyTreeGrid";
import type { SupportedTemplateType } from "~/graphql/supergraphTypes";

export type CreateQueryFormValues = {
  name: string;
  supportedTemplateType: SupportedTemplateType;
  supportedTransactionTypes: number[];
};

const NAME_MAX_LENGTH = 100;
const SUPPORTED_TEMPLATE_TYPE_HELP_TEXT = "Select the template type this query will support";
const NAME_NOT_UNIQUE_ERROR = "Name already taken. Enter a unique name.";

/** Backend treats query names case-insensitively (see `finpub-data-service`
 * uniqueness constraint), so the duplicate check must be case-insensitive
 * too — otherwise the user could submit "Foo" against an existing "foo" and
 * only learn at save time. The Set passed in is already lower-cased by the
 * caller; we lower-case + trim the candidate value here. */
const createValidationSchema = (
  existingNames: Set<string>,
  supportedValues: SupportedTemplateType[],
): ObjectSchema<CreateQueryFormValues> =>
  object({
    name: string()
      .required("Name the query")
      .trim()
      .min(1, "Name the query")
      .max(NAME_MAX_LENGTH, `Maximum ${NAME_MAX_LENGTH} characters`)
      .test("unique-name", NAME_NOT_UNIQUE_ERROR, (value) => {
        if (!value) return true;
        return !existingNames.has(value.trim().toLowerCase());
      }),
    supportedTemplateType: mixed<SupportedTemplateType>()
      .oneOf(supportedValues, "Select a template type")
      .required("Select a template type"),
    supportedTransactionTypes: array()
      .of(number().required())
      .when("supportedTemplateType", {
        is: supportsTransactionTypes,
        then: (schema) => schema.min(1, "Select at least one transaction type"),
      })
      .required(),
  });

export type CreateQueryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (values: CreateQueryFormValues) => Promise<void>;
  isCreating: boolean;
  existingNames?: Set<string>;
  /** Type hierarchy blob from `finpubDataPackage.typeHierarchy` — feeds the
   *  transaction-type node picker. */
  typeHierarchy?: TypeHierarchy | null;
};

export const CreateQueryModal = ({
  isOpen,
  onClose,
  onCreate,
  isCreating,
  existingNames = new Set(),
  typeHierarchy = null,
}: CreateQueryModalProps) => {
  const filteredReportTypeConfig = useAvailableReportTypeOptions();

  // Disabled when only the default option is available; enabled once at least
  // one additional template type is unlocked via a feature flag.
  const isSupportedTemplateTypeDisabled = filteredReportTypeConfig.length === 1;

  const handleSubmit = useCallback(
    async (values: CreateQueryFormValues) => {
      await onCreate({
        ...values,
        supportedTransactionTypes: toTransactionTypeNodeIds(values),
      });
    },
    [onCreate],
  );

  const validationSchema = useMemo(
    () =>
      createValidationSchema(
        existingNames,
        filteredReportTypeConfig.map((o) => o.value),
      ),
    [existingNames, filteredReportTypeConfig],
  );

  const initialValues: CreateQueryFormValues = {
    name: "",
    supportedTemplateType: "ACCOUNT_STATEMENT",
    supportedTransactionTypes: [],
  };

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title="New query"
      data-testid="create-query-modal"
      size="medium"
      isCentered
    >
      <Form<CreateQueryFormValues>
        initialValues={initialValues}
        yupValidationSchema={validationSchema}
        onSubmit={handleSubmit}
        submitOnlyChangedValues={false}
      >
        <FocusFirstFormErrorOnSubmit />
        <ModalV2.Body>
          <Flex flexDirection="column" gap={SPACING.LARGE}>
            <FormTextInput
              name="name"
              label="Query name"
              data-testid="create-query-name"
              isFullWidth
            />
            <FormSelect
              name="supportedTemplateType"
              label="Supported template type"
              helpText={SUPPORTED_TEMPLATE_TYPE_HELP_TEXT}
              data-testid="create-query-supported-template-type"
              disabled={isSupportedTemplateTypeDisabled}
              inModal
              full
            >
              {renderFormSelectOptions(filteredReportTypeConfig)}
            </FormSelect>
            <TransactionTypesField typeHierarchy={typeHierarchy} />
          </Flex>
        </ModalV2.Body>

        <ModalV2.Footer>
          <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
            <SubmitButton isLoading={isCreating} data-testid="create-query-submit">
              Create new query
            </SubmitButton>
          </Flex>
        </ModalV2.Footer>
      </Form>
    </ModalV2>
  );
};
